// K2 · the small window that stays on top (desktop v3 "PiP", D_Desk.dc.html modes idle, remind, pip, hover, glance,
// done; decision record 电脑端-v3 "小窗和菜单栏"). A close-up of her room's camera: the pip still and loop, the glance /
// nod clips (their peak stills with reduced motion). What she shows:
//   not working together  she does her thing + UP NEXT (hover: ✓ / Stuck / Start); nothing planned: "Plan with {n}" /
//                         Later, with her morning line only in the morning on the user's clock (Me → Time zone, not
//                         this computer's); all done: only "n of m done today"
//   a card's time         she looks up with one line + Start together / Later (once per card; the shell sends 'remind')
//   working together      the timer; "{n} is writing too" while keys or the mouse move; after a few quiet minutes she
//                         looks up (no words, the "notice" switch); after longer one soft line, at most twice a session
//   Done                  she nods, a stamp, minutes + points, her line, and the next card
// Where it runs:
//   0.2.0 shell ('pip')   the shell shows it whenever Xiaoman is open and you are in another app ('pip' events say when
//                         and why), sends idle seconds ('activity') and reminders, and does S/M/L, drag and "–"
//   0.1.0 shell ('old')   opened only while working together, at 360x560, with none of those events: the same view fills
//                         it, "–" is its old tuck-to-the-edge tab (56x140), no activity looks, reminders stay toasts
//   a browser ('web')     the same view; buttons act in the page (the room is reset('C'))
// The session is the room's (lib/deskSession.ts), so both windows agree on what is being worked on.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Card, Reward } from '@shared/types';
import { C } from '@shared/content/characters';
import { LINES, fill } from '@shared/content/lines';
import { isBigReward, isMorningAt, taskShort } from '@shared/rules';
import { appStore, useApp, doneCard, startCard, stuckChat, rewardSeen } from '../../store';
import { reset } from '../../router';
import { desktop, keepAwake, onNative, type DeskPrefs, type PipSize, type PipState } from '../../native';
import { Screen, XFace } from '../../components';
import { useReducedMotion } from '../../layout';
import { deskSession, deskTimeAt, readStopped, unstop, STOPPED_KEY } from '../../lib/deskSession';
import { timerMinutes, track } from '../../lib/analytics';
import { deskAsset } from './media';
import { PIP } from './pipCopy';
import s from './pip.module.css';

type Host = 'pip' | 'old' | 'web';
type Mode = 'work' | 'typing' | 'glance' | 'nudge' | 'idle' | 'remind' | 'noplan' | 'alldone' | 'done' | 'big';
type Flash = { kind: 'remind'; cardId: string } | { kind: 'done'; reward: Reward; next: string | null };
interface Btn { label: string; aria: string; go: () => void; strong?: boolean; f: number; long?: boolean; busy?: boolean }

const GLANCE_AFTER = 180; // s without a key or mouse input: she looks up (D_Desk demo "停手几分钟", 10 s there)
const NUDGE_AFTER = 600;  // s: one soft line ("停得更久"), at most twice a session
const TYPING_IDLE = 1;    // s: input this recent is "{n} is writing too" (it drops back ~1.6 s after the last key)
const REACT_MS = 7500;    // a reaction clip stays mounted this long, fading back to her loop at reactEnd
const reactEnd = (ch: string) => (ch === 'arjun' ? 5800 : 4850);
const FRESH_MS = 120000;  // a Done made elsewhere (menu bar, phone) still counts as news this long
const SIZES: PipSize[] = ['s', 'm', 'l'];
const DEF_PREFS: DeskPrefs = { stay: true, notice: true, meetings: true };
const QUIET_KEY = 'xm.pip.noplanQuiet'; // "Later" when nothing is planned: quiet for the rest of that day

const getLocal = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const setLocal = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage blocked */ } };
const clock = (ms: number) => {
  const t = Math.max(0, Math.floor(ms / 1000)), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  const p = (n: number) => (n < 10 ? '0' : '') + n;
  return h ? `${h}:${p(m)}:${p(sec)}` : `${m}:${p(sec)}`;
};
const rewardKey = (r: Reward) => `${r.id}@${r.doneAt}`;

export function MiniWindow() {
  const st = useApp((x) => x.state);
  const signedIn = useApp((x) => !!x.token);
  const reduced = useReducedMotion();
  const host: Host = desktop.hasPip() ? 'pip' : desktop.isMini() ? 'old' : 'web';

  const [pip, setPip] = useState<PipState | null>(null);
  const [idle, setIdle] = useState<number | null>(null);
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [look, setLook] = useState<{ kind: 'glance' | 'nudge'; line: number } | null>(null);
  const [react, setReact] = useState<{ kind: 'glance' | 'nod'; t: number } | null>(null);
  const [reactOn, setReactOn] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [stopped, setStopped] = useState<string[]>(readStopped);
  const [quietDay, setQuietDay] = useState<string | null>(() => getLocal(QUIET_KEY));
  const [tucked, setTucked] = useState(false);
  const [vw, setVw] = useState(() => window.innerWidth);
  const narrow = vw < 120;
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  const [everShown, setEverShown] = useState(host !== 'pip');
  const [dragging, setDragging] = useState(false);
  const [now, setNow] = useState(Date.now());
  const loopRef = useRef<HTMLVideoElement | null>(null);
  const seen = useRef<string | null>(st?.reward ? rewardKey(st.reward) : null); // rewards from before this window opened
  const reminded = useRef(new Set<string>());
  const stretch = useRef({ key: '', nudges: 0, glanced: false, nudged: false });
  const drag = useRef<{ id: number; x: number; y: number; moved: boolean; raf: number } | null>(null);

  const shown = host === 'pip' ? !!pip?.shown : true;
  const prefs = pip?.prefs || DEF_PREFS;

  // ---------------------------------------------------------------- shell events and storage shared with the room
  const lookUp = () => setReact({ kind: 'glance', t: Date.now() });
  const nod = () => setReact({ kind: 'nod', t: Date.now() });
  const endFlash = (f: Flash | null, seenIt: boolean) => {
    if (f && f.kind === 'done' && seenIt) void rewardSeen();
    setFlash(null);
  };
  const flashRef = useRef<Flash | null>(null);
  flashRef.current = flash;

  useEffect(() => {
    const off = onNative((e) => {
      if (e.type === 'pip') {
        setPip(e.state);
        if (e.state.shown) setEverShown(true);
        else if (e.state.why === 'room') {
          // Back in her room: what the small window had to say is over (D_Desk setFront('room')).
          endFlash(flashRef.current, true);
          setLook(null);
        }
      } else if (e.type === 'desk-prefs') setPip((p) => (p ? { ...p, prefs: e.prefs } : p));
      else if (e.type === 'activity') setIdle(e.idle);
      else if (e.type === 'remind') {
        const cur = appStore.get().state;
        if (!cur || reminded.current.has(e.cardId) || deskSession(cur, readStopped())) return;
        const cd = cur.plan.cards.find((x) => x.id === e.cardId && x.st === 'todo');
        if (!cd || flashRef.current?.kind === 'done') return;
        reminded.current.add(e.cardId);
        setFlash({ kind: 'remind', cardId: cd.id });
        lookUp();
      }
    });
    if (host === 'pip') void desktop.pipState().then((p) => { if (p) { setPip(p); if (p.shown) setEverShown(true); } });
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === STOPPED_KEY || ev.key == null) setStopped(readStopped());
      if (ev.key === QUIET_KEY || ev.key == null) setQuietDay(getLocal(QUIET_KEY));
    };
    const onResize = () => setVw(window.innerWidth);
    const onVis = () => setVisible(document.visibilityState !== 'hidden');
    window.addEventListener('storage', onStorage);
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => { off(); window.removeEventListener('storage', onStorage); window.removeEventListener('resize', onResize); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // ---------------------------------------------------------------- the day, from the server state
  const skew = useMemo(() => (st ? Date.parse(st.now) - Date.now() : 0), [st?.now]);
  const serverNow = now + (Number.isFinite(skew) ? skew : 0);
  const live = st ? st.plan.cards.filter((x) => x.st !== 'removed' && x.st !== 'moved') : [];
  const todo = live.filter((x) => x.st === 'todo');
  const next = todo[0] || null;
  const session = st ? deskSession(st, stopped) : null;
  const sessKey = session ? `${session.id}@${session.startedAt}` : '';
  const remCard = flash?.kind === 'remind' ? todo.find((x) => x.id === flash.cardId) || null : null;

  // Clock: every second while a session's timer shows, else twice a minute (day / night, freshness).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), session && shown && visible ? 1000 : 30000);
    return () => clearInterval(t);
  }, [!!session, shown, visible]);
  // Keep the screen on while working together and she is on screen (like Working together on the phone).
  useEffect(() => { const on = !!session && shown; void keepAwake(on); return () => { if (on) void keepAwake(false); }; }, [!!session, shown]);
  // A reaction clip plays once, fades back to her loop, then goes.
  useEffect(() => {
    if (!react) return undefined;
    setReactOn(true);
    const a = setTimeout(() => setReactOn(false), reactEnd(st?.ch || 'margo'));
    const b = setTimeout(() => setReact(null), REACT_MS);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [react?.t]);
  // Her loop plays only while she is on screen.
  useEffect(() => {
    const v = loopRef.current;
    if (!v) return;
    if (shown && visible) void v.play().catch(() => undefined); else v.pause();
  }, [shown, visible, everShown, st?.ch]);
  // A new session: new nudges; her looks start over.
  useEffect(() => { stretch.current = { key: sessKey, nudges: 0, glanced: false, nudged: false }; setLook(null); }, [sessKey]);
  // A reminder for a card that is no longer to do (done on the phone, moved) goes.
  useEffect(() => { if (flash?.kind === 'remind' && !remCard) setFlash(null); }, [flash, remCard]);

  // Activity (0.2.0 shells): typing / moving the mouse, a few quiet minutes, a longer quiet stretch.
  useEffect(() => {
    if (idle == null || !session) return;
    const k = stretch.current;
    if (idle <= TYPING_IDLE) { k.glanced = false; k.nudged = false; if (look) setLook(null); return; }
    if (!prefs.notice) return;
    if (idle >= NUDGE_AFTER && !k.nudged) {
      k.nudged = true;
      if (k.nudges < 2 && !pip?.quiet) { k.nudges += 1; setLook({ kind: 'nudge', line: k.nudges - 1 }); lookUp(); return; }
    }
    if (idle >= GLANCE_AFTER && !k.glanced) { k.glanced = true; if (look?.kind !== 'nudge') { setLook({ kind: 'glance', line: 0 }); lookUp(); } }
  }, [idle, sessKey, prefs.notice]);

  // A Done made elsewhere while she is on screen (the menu bar's Done, the phone): she nods here too.
  const showDone = (rw: Reward) => {
    seen.current = rewardKey(rw);
    const cur = appStore.get().state;
    const nx = cur ? cur.plan.cards.filter((x) => x.st === 'todo' && x.id !== rw.id)[0] || null : null;
    setFlash({ kind: 'done', reward: rw, next: nx ? nx.id : null });
    setLook(null);
    nod();
  };
  useEffect(() => {
    const rw = st?.reward;
    if (!rw || rewardKey(rw) === seen.current) return;
    seen.current = rewardKey(rw);
    if (!shown || (flash?.kind === 'done' && rewardKey(flash.reward) === rewardKey(rw))) return;
    if (Math.abs(serverNow - Date.parse(rw.doneAt)) > FRESH_MS) return;
    showDone(rw);
  }, [st?.reward ? rewardKey(st.reward) : '']);

  if (!signedIn) {
    return (
      <Screen theme="margo" id="K2" label="Xiaoman">
        <div class={s.msg}>
          <p>{PIP.signedOut}</p>
          <button type="button" class={s.open} onClick={() => { if (host === 'web') reset('O1'); else desktop.focusMain(); }}>{PIP.open}</button>
        </div>
      </Screen>
    );
  }
  if (!st) return <Screen theme="margo" id="K2" />;

  const ch = st.ch, c = C[ch], name = c.name;
  // Her clock is the user's: the time zone set in Me (st.user.tz) on the server's clock, never this computer's zone.
  const time = deskTimeAt(serverNow, st.user.tz);
  const morning = isMorningAt(serverNow, st.user.tz);
  const typing = host === 'pip' && idle != null && idle <= TYPING_IDLE && !hover;

  let mode: Mode;
  if (flash?.kind === 'done') mode = isBigReward(flash.reward) ? 'big' : 'done';
  else if (session) mode = look ? look.kind : typing ? 'typing' : 'work';
  else if (remCard) mode = 'remind';
  else if (todo.length) mode = 'idle';
  else if (live.length) mode = 'alldone';
  else mode = 'noplan';
  const quiet = quietDay === st.today;
  const lookedUp = mode === 'glance' || mode === 'nudge' || mode === 'remind';
  const sessionLike = mode === 'work' || mode === 'typing' || mode === 'glance' || mode === 'nudge';
  const ambient = mode === 'idle' || mode === 'remind' || mode === 'noplan' || mode === 'alldone';
  const rw = flash?.kind === 'done' ? flash.reward : null;
  const nextAfter = flash?.kind === 'done' && flash.next ? todo.find((x) => x.id === flash.next) || null : null;
  const sessTitle = session ? session.alt || session.title : '';
  const sessClock = session && session.startedAt ? clock(serverNow - Date.parse(session.startedAt)) : '';

  // ---------------------------------------------------------------- actions
  const toRoom = () => {
    if (host === 'web') { reset('C'); return; }
    desktop.focusMain();
    if (host === 'old' && !session) desktop.closeMini(); // the 0.1.0 cowork window has nothing left to show
  };
  const start = async (x: Card) => {
    if (busy) return;
    endFlash(flash, true);
    setLook(null);
    if (stopped.includes(x.id)) { unstop(x.id); setStopped(readStopped()); }
    if (x.startedAt) return; // started before and stopped in the room: it goes on, as the room's Start together does
    setBusy('start:' + x.id);
    await startCard(x.id, null, 'next'); // the next card, or the one whose time came
    setBusy(null);
  };
  const finish = async (x: Card, how: 'tick' | 'work') => {
    if (busy) return;
    setBusy('done:' + x.id);
    const r = await doneCard(x.id, { from: how === 'work' ? 'work-chat' : 'chat' });
    setBusy(null);
    if (r && how === 'work') track('cowork_end', { card: x.id, minutes: timerMinutes(x.startedAt, serverNow), how: 'done' });
    if (r) showDone(r);
  };
  const stuck = async (x: Card) => {
    if (busy) return;
    const working = !!session && session.id === x.id;
    if (working) track('cowork_end', { card: x.id, minutes: timerMinutes(x.startedAt, serverNow), how: 'stuck' });
    setBusy('stuck:' + x.id);
    const r = await stuckChat(x.id, null, working ? 'cowork' : 'next'); // she asks what's in the way, in the room's subtitles
    setBusy(null);
    if (r) toRoom();
  };
  const notNow = () => { endFlash(flash, true); if (host === 'old' && !session) desktop.closeMini(); };
  const seeIt = () => {
    const f = flash;
    setFlash(null);
    if (host === 'web') { reset('C'); return; }
    if (f?.kind === 'done') desktop.focusMain('R:' + f.reward.id); // her celebration in the room
  };
  const hide = () => { if (host === 'pip') desktop.hidePip(); else { if (host === 'old') desktop.tuck(true); setTucked(true); } };
  const untuck = () => { if (host === 'old') desktop.tuck(false); setTucked(false); };
  const size = pip?.size || 'm';
  const vwSize: PipSize = vw < 220 ? 's' : vw < 290 ? 'm' : 'l'; // 0.1.0 shell / browser: from the window
  const cycleSize = () => desktop.setPipSize(SIZES[(SIZES.indexOf(size) + 1) % SIZES.length]);

  // ---------------------------------------------------------------- what she shows (D_Desk pipBtns / STATUS / bubble)
  const btns: Btn[] = [];
  const b = (label: string, go: () => void, o: Partial<Btn> = {}): Btn => ({ label, aria: o.aria || label, go, f: 1, ...o });
  if (mode === 'done' || mode === 'big') {
    if (mode === 'big') btns.push(b(PIP.btn.seeIt, seeIt, { strong: true }));
    if (nextAfter) btns.push(b(fill(PIP.btn.next, { Task: taskShort(nextAfter.title) }), () => void start(nextAfter), { strong: mode !== 'big', f: 2, long: true, aria: fill(PIP.aria.next, { title: nextAfter.title }), busy: busy === 'start:' + nextAfter.id }));
    if (btns.length < 2) btns.push(b(PIP.btn.notNow, notNow));
  } else if (mode === 'remind' && remCard) {
    btns.push(b(PIP.btn.startTogether, () => void start(remCard), { strong: true, f: 1.7, long: true, aria: fill(PIP.aria.startTogether, { title: remCard.title }), busy: busy === 'start:' + remCard.id }));
    btns.push(b(PIP.btn.later, () => setFlash(null), { aria: PIP.aria.later }));
  } else if (mode === 'noplan') {
    btns.push(b(fill(PIP.btn.plan, { n: name }), toRoom, { strong: true, f: 1.8, long: true }));
    if (!quiet) btns.push(b(PIP.btn.later, () => { setLocal(QUIET_KEY, st.today); setQuietDay(st.today); }));
  } else if (mode === 'idle' && next) {
    btns.push(b(PIP.btn.tick, () => void finish(next, 'tick'), { f: 0.6, aria: fill(PIP.aria.tick, { title: next.title }), busy: busy === 'done:' + next.id }));
    btns.push(b(PIP.btn.stuck, () => void stuck(next), { aria: fill(PIP.aria.stuck, { title: next.title }), busy: busy === 'stuck:' + next.id }));
    btns.push(b(PIP.btn.start, () => void start(next), { strong: true, f: 1.2, aria: fill(PIP.aria.startTogether, { title: next.title }), busy: busy === 'start:' + next.id }));
  } else if (sessionLike && session) {
    btns.push(b(PIP.btn.stuck, () => void stuck(session), { busy: busy === 'stuck:' + session.id }));
    btns.push(b(PIP.btn.done, () => void finish(session, 'work'), { strong: true, f: 1.3, aria: fill(PIP.aria.done, { title: sessTitle }), busy: busy === 'done:' + session.id }));
  }
  const over = hover || focused;
  const controls = over ? btns.length > 0 : mode === 'done' || mode === 'big' || mode === 'remind' || (mode === 'noplan' && !quiet);

  const status = (() => {
    const v = { n: name, act: PIP.act[ch] };
    if (rw) return fill(PIP.status.done, { mins: rw.direct ? '' : ` · ${rw.minutes} min`, pts: rw.gained });
    if (mode === 'remind' && remCard) return fill(PIP.status.remind, { n: name, time: remCard.time });
    if (mode === 'alldone') return fill(PIP.status.alldone, { d: live.filter((x) => x.st === 'done').length, t: live.length });
    if (mode === 'idle' || mode === 'noplan') return fill(PIP.status.idle, v);
    return fill(PIP.status[mode as 'work' | 'typing' | 'glance' | 'nudge'], v);
  })();
  const bubble = (() => {
    if (mode === 'nudge' && look) return PIP.nudge[ch][look.line % 2];
    if (mode === 'done' && rw) return rw.line;
    if (mode === 'big' && rw) return rw.streakTo != null ? fill(LINES[ch].streak, { k: rw.streakTo }) : rw.perfect ? PIP.perfect[ch] : LINES[ch].levelUp;
    if (mode === 'remind' && remCard) return fill(PIP.remind[ch], { time: remCard.time, Task: taskShort(remCard.title) });
    // "Morning! …" only in the morning (4 AM to noon) on her clock; later in the day nothing planned has no line.
    if (mode === 'noplan' && !quiet && morning) return LINES[ch].morning;
    return '';
  })();
  const stamp = mode === 'big' && rw ? (rw.streakTo != null ? `DAY ${rw.streakTo}` : rw.perfect ? 'PERFECT' : `LV ${rw.levelTo}`) : mode === 'done' ? 'DONE' : '';
  const nextLabel = mode === 'idle' && next ? fill(PIP.label.upNext, { time: next.time })
    : mode === 'remind' && remCard ? fill(PIP.label.now, { time: remCard.time })
    : mode === 'noplan' ? PIP.label.noPlan : PIP.label.allDone;
  const nextTitle = mode === 'idle' && next ? next.title
    : mode === 'remind' && remCard ? remCard.title
    : mode === 'noplan' ? fill(PIP.title.noPlan, { n: name }) : fill(PIP.title.allDone, { k: st.streak.count });

  // ---------------------------------------------------------------- media (stills under the videos, always)
  const motion = !reduced && everShown;
  const still = deskAsset(ch, time, 'pip');
  const peak = reduced ? (lookedUp ? deskAsset(ch, time, 'glance_s') : mode === 'done' || mode === 'big' ? deskAsset(ch, time, 'nod_s') : '') : '';

  // ---------------------------------------------------------------- the 0.1.0 shell's tab, or a browser's
  if ((host === 'old' && narrow) || (host === 'web' && tucked)) {
    return (
      <Screen theme={ch} id="K2" bg="transparent">
        <button type="button" class={s.tab} aria-label={fill(PIP.aria.untuck, { n: name })} onClick={untuck}>
          <XFace ch={ch} e={3} s={narrow ? 36 : 42} ring="var(--acc)" alt="" />
          {session && <span>{sessClock}</span>}
        </button>
      </Screen>
    );
  }

  // Drag (0.2.0 shell): the page reports the pointer; the shell moves the window under it.
  const onDown = (e: PointerEvent) => {
    if (host !== 'pip' || e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
    drag.current = { id: e.pointerId, x: e.screenX, y: e.screenY, moved: false, raf: 0 };
    desktop.pipDrag('start');
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.moved && Math.hypot(e.screenX - d.x, e.screenY - d.y) < 3) return;
    if (!d.moved) { d.moved = true; setDragging(true); }
    if (!d.raf) d.raf = requestAnimationFrame(() => { d.raf = 0; desktop.pipDrag('move'); });
  };
  const onUp = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (d.raf) cancelAnimationFrame(d.raf);
    drag.current = null;
    setDragging(false);
    desktop.pipDrag('end');
  };

  const utils = host === 'pip' ? 3 : 2;
  return (
    <Screen theme={ch} id="K2" label={fill(PIP.aria.window, { n: name })}>
      <div
        class={s.pip} data-mode={mode} data-host={host} data-size={host === 'pip' ? size : vwSize} data-dragging={dragging ? '' : undefined}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        onFocusIn={() => setFocused(true)} onFocusOut={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setFocused(false); }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      >
        <div class={s.media} aria-hidden="true">
          <img class={s.fill} src={still} alt="" draggable={false} />
          {motion && <video ref={loopRef} key={`${ch}-${time}`} class={s.fill} src={deskAsset(ch, time, 'piploop')} poster={still} autoPlay muted loop playsInline />}
          {motion && react && (
            <video key={react.t} class={`${s.fill} ${s.react}`} src={deskAsset(ch, time, react.kind)} autoPlay muted playsInline style={{ opacity: reactOn ? 1 : 0 }} />
          )}
          {peak && <img class={s.fill} src={peak} alt="" draggable={false} />}
        </div>
        <div class={s.shade} aria-hidden="true" />

        <div class={s.top}>
          <span class={s.pill} data-part="pill" data-utils={over ? '' : undefined} style={{ ['--utils-w' as string]: utils === 3 ? '104px' : '70px' }}>
            <span class={s.dot} data-up={lookedUp ? '' : undefined} aria-hidden="true" />
            <span class={s.pillT}>{status}</span>
          </span>
        </div>
        {over && (
          <div class={s.utils} data-part="utils">
            {host === 'pip' && <button type="button" class={`${s.util} ${s.size}`} aria-label={fill(PIP.aria.size, { s: size.toUpperCase() })} onClick={cycleSize}>{size.toUpperCase()}</button>}
            <button type="button" class={s.util} aria-label={fill(PIP.aria.room, { n: name })} onClick={toRoom}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" /></svg>
            </button>
            <button type="button" class={s.util} aria-label={host === 'pip' ? (desktop.isMac() ? PIP.aria.hideMac : PIP.aria.hideWin) : PIP.aria.tuck} onClick={hide}>
              <svg viewBox="0 0 24 24" aria-hidden="true" style={{ strokeWidth: 2.4 }}><path d="M6 12h12" /></svg>
            </button>
          </div>
        )}
        {stamp && <span class={s.stamp} data-part="stamp" aria-hidden="true">{stamp}</span>}

        <div class={s.bottom}>
          {bubble && <p class={s.bubble} data-part="bubble" aria-live="polite" key={bubble}>{bubble}</p>}
          {sessionLike && (
            <div class={s.clockRow}>
              <span class={s.clockT}>{sessTitle}</span>
              <span class={s.clock} role="timer" aria-label={fill(PIP.aria.timer, { t: sessClock })}>{sessClock}</span>
            </div>
          )}
          {ambient && (
            <div class={s.next}>
              <span class={s.nextL}>{nextLabel}</span>
              <span class={s.nextT}>{nextTitle}</span>
            </div>
          )}
          {controls && (
            <div class={s.btns} data-long={btns.some((x) => x.long) ? '' : undefined}>
              {btns.map((x) => (
                <button
                  type="button" key={x.label} class={s.btn} aria-label={x.aria} style={{ ['--f' as string]: x.f }}
                  data-strong={x.strong ? '' : undefined} data-long={x.long ? '' : undefined} aria-busy={x.busy ? 'true' : undefined}
                  data-cta={x.strong ? 'pip' : undefined} onClick={x.go}
                ><span>{x.label}</span></button>
              ))}
            </div>
          )}
        </div>
        <div class={s.edge} aria-hidden="true" />
      </div>
    </Screen>
  );
}
